import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { Link } from "react-router-dom";
import { UserPlus } from "lucide-react";
import OverviewTab from "../components/admin/OverviewTab";
import ProductsTab from "../components/admin/ProductsTab";
import OrdersTab from "../components/admin/OrdersTab";
import UsersTab from "../components/admin/UsersTab";
import TeamTab from "../components/admin/TeamTab";
import SiteContentTab from "../components/admin/SiteContentTab";

export default function AdminDashboard() {
  const { API, user, authHeaders, refreshContent } = useApp();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadAll = () => {
    axios.get(`${API}/admin/stats`, { headers: authHeaders() }).then(r=>setStats(r.data)).catch(()=>{});
    axios.get(`${API}/products`).then(r=>setProducts(r.data));
    axios.get(`${API}/orders`, { headers: authHeaders() }).then(r=>setOrders(r.data)).catch(()=>{});
    if (user?.role === "owner") {
      axios.get(`${API}/admin/invites`, { headers: authHeaders() }).then(r=>setInvites(r.data)).catch(()=>{});
      axios.get(`${API}/admin/users`, { headers: authHeaders() }).then(r=>setUsers(r.data)).catch(()=>{});
      axios.get(`${API}/admin/settings`, { headers: authHeaders() }).then(r=>setSettings(r.data)).catch(()=>{});
    }
  };
  useEffect(() => { if (user) loadAll(); /* eslint-disable-next-line */ }, [user]);

  // Auto-redirect owner to setup wizard on first login
  useEffect(() => {
    if (settings && user?.role === "owner" && !settings.onboarding_completed) {
      const already = sessionStorage.getItem("lcw_wizard_dismissed");
      if (!already) window.location.href = "/admin/setup";
    }
  }, [settings, user]);

  if (!user) return <div className="p-20 text-center"><Link to="/login" className="btn-primary">SIGN IN</Link></div>;
  if (user.role !== "admin" && user.role !== "owner") return <div className="p-20 text-center font-mono" data-testid="access-denied">Access denied. Admins only.</div>;

  const visibleTabs = ["overview", "products", "orders", "users", "team", "content"]
    .filter(t => !((t === "users" || t === "team" || t === "content") && user.role !== "owner"));

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="font-mono text-xs text-[var(--accent)] mb-1">ADMIN · {user.role.toUpperCase()}</div>
          <h1 className="font-display text-5xl">DASHBOARD</h1>
        </div>
        {user.role === "owner" && (
          <Link to="/admin/settings" className="btn-outline inline-flex items-center gap-2" data-testid="settings-link">
            <UserPlus size={14}/> STORE SETTINGS
          </Link>
        )}
      </div>

      {settings && user.role === "owner" && !settings.onboarding_completed && (
        <Link to="/admin/setup" className="block mb-6 card-industrial p-4 bg-[var(--accent-2)] hover:bg-[var(--accent)] hover:text-white transition" data-testid="wizard-banner">
          <div className="font-display">▸ FINISH STORE SETUP</div>
          <div className="font-mono text-xs">Connect Square (card + Cash App), email, SMS, and shipping — takes 2 minutes.</div>
        </Link>
      )}

      <div className="flex gap-2 mb-8 border-b-2 border-[var(--ink)] overflow-x-auto" data-testid="admin-tabs">
        {visibleTabs.map(t => (
          <button key={t} onClick={()=>setTab(t)} className={`px-6 py-3 font-mono text-xs uppercase tracking-wider ${tab===t?'bg-[var(--ink)] text-[var(--paper)]':''}`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab stats={stats}/>}
      {tab === "products" && <ProductsTab products={products} API={API} authHeaders={authHeaders} onReload={loadAll}/>}
      {tab === "orders" && <OrdersTab orders={orders} API={API} authHeaders={authHeaders} onReload={loadAll}/>}
      {tab === "users" && user.role === "owner" && <UsersTab users={users} currentUser={user} API={API} authHeaders={authHeaders} onReload={loadAll}/>}
      {tab === "team" && user.role === "owner" && <TeamTab invites={invites} API={API} authHeaders={authHeaders} onReload={loadAll}/>}
      {tab === "content" && user.role === "owner" && <SiteContentTab API={API} authHeaders={authHeaders} refreshContent={refreshContent}/>}
    </div>
  );
}
